<script>
import { useAlert } from 'dashboard/composables';
import PageHeader from '../../SettingsSubPageHeader.vue';
import router from '../../../../index';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { mapGetters } from 'vuex';

const DAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const DAY_LABELS = {
  monday: 'MONDAY',
  tuesday: 'TUESDAY',
  wednesday: 'WEDNESDAY',
  thursday: 'THURSDAY',
  friday: 'FRIDAY',
  saturday: 'SATURDAY',
  sunday: 'SUNDAY',
};

const BR_TIMEZONES = [
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT -03:00)' },
  { value: 'America/Manaus', label: 'America/Manaus (AMT -04:00)' },
  { value: 'America/Rio_Branco', label: 'America/Rio_Branco (ACT -05:00)' },
  { value: 'America/Noronha', label: 'America/Noronha (FNT -02:00)' },
  { value: 'America/Cuiaba', label: 'America/Cuiaba (AMT -04:00)' },
  { value: 'America/Belem', label: 'America/Belem (BRT -03:00)' },
];

const DEFAULT_CONFIG = () => {
  const config = {};
  DAY_ORDER.forEach(day => {
    const isSunday = day === 'sunday';
    config[day] = {
      enabled: !isSunday,
      periods: isSunday ? [] : [{ start: '08:00', end: '18:00' }],
    };
  });
  return config;
};

export default {
  components: {
    PageHeader,
    // eslint-disable-next-line vue/no-reserved-component-names
    Switch,
    NextButton,
  },
  props: {
    mode: {
      type: String,
      default: 'create',
      validator: v => ['create', 'edit'].includes(v),
    },
  },
  data() {
    return {
      businessHoursEnabled: false,
      timezone: 'America/Sao_Paulo',
      scheduleConfig: DEFAULT_CONFIG(),
      outsideHoursMessage: '',
      fallbackTeamId: null,
      fallbackClosedAction: 'keep_fallback',
      respectHolidays: true,
      assignOutsideHours: false,
      isSubmitting: false,
      DAY_ORDER,
      DAY_LABELS,
      BR_TIMEZONES,
    };
  },
  computed: {
    ...mapGetters({
      teams: 'teams/getTeams',
    }),
    teamId() {
      return this.$route.params.teamId;
    },
    teamData() {
      return this.$store.getters['teams/getTeam'](this.teamId);
    },
    otherTeams() {
      return this.teams.filter(t => String(t.id) !== String(this.teamId));
    },
    isCreateMode() {
      return this.mode === 'create';
    },
    headerTitle() {
      return this.isCreateMode
        ? this.$t('TEAMS_SETTINGS.BUSINESS_HOURS.TITLE')
        : this.$t('TEAMS_SETTINGS.BUSINESS_HOURS.TITLE');
    },
    headerContent() {
      return this.$t('TEAMS_SETTINGS.BUSINESS_HOURS.SUBTITLE');
    },
    currentTeamName() {
      return this.teamData?.name || '';
    },
    fallbackTeamName() {
      if (!this.fallbackTeamId) return '';
      const team = this.teams.find(
        t => String(t.id) === String(this.fallbackTeamId)
      );
      return team?.name || '';
    },
    nextLabel() {
      return this.isCreateMode
        ? this.$t('TEAMS_SETTINGS.BUSINESS_HOURS.NEXT')
        : this.$t('TEAMS_SETTINGS.BUSINESS_HOURS.SAVE_NEXT');
    },
  },
  watch: {
    teamData(val) {
      if (!this.isCreateMode && val) {
        this.populateFromTeam();
      }
    },
  },
  mounted() {
    this.$store.dispatch('teams/get');
    if (!this.isCreateMode && this.teamData) {
      this.populateFromTeam();
    }
  },
  methods: {
    populateFromTeam() {
      const t = this.teamData;
      if (!t) return;

      if (t.business_hours_enabled != null) {
        this.businessHoursEnabled = t.business_hours_enabled;
      }
      if (t.timezone) {
        this.timezone = t.timezone;
      }
      // GK-HUB: Convert backend format (closed) to frontend format (enabled)
      if (
        t.business_hours_config &&
        Object.keys(t.business_hours_config).length > 0
      ) {
        const frontendConfig = {};
        DAY_ORDER.forEach(day => {
          const backendDay = t.business_hours_config[day];
          if (backendDay) {
            frontendConfig[day] = {
              enabled: !backendDay.closed,
              periods: backendDay.periods || [],
            };
          } else {
            frontendConfig[day] = { enabled: false, periods: [] };
          }
        });
        this.scheduleConfig = frontendConfig;
      }
      if (t.outside_hours_message != null) {
        this.outsideHoursMessage = t.outside_hours_message;
      }
      if (t.fallback_team_id != null) {
        this.fallbackTeamId = t.fallback_team_id;
      }
      if (t.fallback_closed_action) {
        this.fallbackClosedAction = t.fallback_closed_action;
      }
      if (t.respect_holidays != null) {
        this.respectHolidays = t.respect_holidays;
      }
      if (t.assign_outside_hours != null) {
        this.assignOutsideHours = t.assign_outside_hours;
      }
    },
    toggleDay(day) {
      const d = this.scheduleConfig[day];
      d.enabled = !d.enabled;
      if (d.enabled && d.periods.length === 0) {
        d.periods.push({ start: '08:00', end: '18:00' });
      }
    },
    addPeriod(day) {
      this.scheduleConfig[day].periods.push({ start: '12:00', end: '18:00' });
    },
    removePeriod(day, index) {
      this.scheduleConfig[day].periods.splice(index, 1);
    },
    handleEnableToggle() {
      if (this.businessHoursEnabled) {
        // Check if config is empty (all disabled), pre-fill with defaults
        const allDisabled = DAY_ORDER.every(
          d => !this.scheduleConfig[d].enabled
        );
        if (allDisabled) {
          this.scheduleConfig = DEFAULT_CONFIG();
        }
      }
    },
    async handleSkip() {
      this.navigateNext();
    },
    async handleSubmit() {
      this.isSubmitting = true;
      try {
        // GK-HUB: Convert frontend format (enabled) to backend format (closed)
        const configForBackend = {};
        DAY_ORDER.forEach(day => {
          const dayData = this.scheduleConfig[day];
          configForBackend[day] = {
            closed: !dayData.enabled,
            periods: dayData.enabled ? dayData.periods : [],
          };
        });

        const payload = {
          business_hours_enabled: this.businessHoursEnabled,
          timezone: this.timezone,
          business_hours_config: configForBackend,
          outside_hours_message: this.outsideHoursMessage,
          fallback_team_id: this.fallbackTeamId,
          fallback_closed_action: this.fallbackClosedAction,
          respect_holidays: this.respectHolidays,
          assign_outside_hours: this.assignOutsideHours,
        };

        await this.$store.dispatch('teams/update', {
          id: this.teamId,
          ...payload,
        });

        this.navigateNext();
      } catch (error) {
        useAlert(this.$t('TEAMS_SETTINGS.TEAM_FORM.ERROR_MESSAGE'));
      } finally {
        this.isSubmitting = false;
      }
    },
    navigateNext() {
      if (this.isCreateMode) {
        router.replace({
          name: 'settings_teams_add_agents',
          params: { page: 'new', teamId: this.teamId },
        });
      } else {
        router.replace({
          name: 'settings_teams_edit_members',
          params: { page: 'edit', teamId: this.teamId },
        });
      }
    },
    handleBack() {
      if (this.isCreateMode) {
        router.replace({
          name: 'settings_teams_new',
        });
      } else {
        router.replace({
          name: 'settings_teams_edit',
          params: { teamId: this.teamId },
        });
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6 overflow-y-auto">
    <PageHeader :header-title="headerTitle" :header-content="headerContent" />

    <div class="flex flex-col gap-5 max-w-3xl">
      <!-- ══════════════════════════════════════ -->
      <!-- SECTION 1: Schedule                    -->
      <!-- ══════════════════════════════════════ -->
      <div
        class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
      >
        <!-- Header: Enable toggle -->
        <div class="flex flex-col gap-1 items-start px-4 py-3">
          <div class="flex justify-between items-center w-full">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('TEAMS_SETTINGS.BUSINESS_HOURS.TITLE') }}
            </h3>
            <Switch
              v-model="businessHoursEnabled"
              @change="handleEnableToggle"
            />
          </div>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ $t('TEAMS_SETTINGS.BUSINESS_HOURS.ENABLED') }}
          </p>
        </div>

        <!-- Config (visible when enabled) -->
        <div v-if="businessHoursEnabled" class="px-4 py-3">
          <div class="flex flex-col gap-4">
            <!-- Timezone -->
            <div class="flex flex-col gap-1 max-w-xs">
              <label class="text-xs font-medium text-n-slate-12">
                {{ $t('TEAMS_SETTINGS.BUSINESS_HOURS.TIMEZONE') }}
              </label>
              <div class="isp-select-wrap">
                <select v-model="timezone" class="isp-select">
                  <option
                    v-for="tz in BR_TIMEZONES"
                    :key="tz.value"
                    :value="tz.value"
                  >
                    {{ tz.label }}
                  </option>
                </select>
                <span class="isp-select-arrow i-lucide-chevron-down" />
              </div>
            </div>

            <!-- Schedule Grid -->
            <div class="flex flex-col gap-0.5">
              <div
                v-for="day in DAY_ORDER"
                :key="day"
                class="grid items-start gap-3 px-3 py-2.5 rounded-md transition-colors bh-day-row"
                :class="[
                  scheduleConfig[day].enabled
                    ? 'bg-n-solid-1 hover:bg-n-alpha-black2'
                    : 'opacity-50',
                ]"
              >
                <!-- Day name -->
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                <div class="text-sm font-semibold text-n-slate-12 pt-1.5">
                  {{
                    $t('TEAMS_SETTINGS.BUSINESS_HOURS.DAYS.' + DAY_LABELS[day])
                  }}
                </div>

                <!-- Toggle -->
                <div class="pt-0.5">
                  <Switch
                    :model-value="scheduleConfig[day].enabled"
                    size="small"
                    @update:model-value="toggleDay(day)"
                  />
                </div>

                <!-- Periods or Closed label -->
                <div class="flex flex-col gap-1.5">
                  <template v-if="scheduleConfig[day].enabled">
                    <div
                      v-for="(period, pIdx) in scheduleConfig[day].periods"
                      :key="pIdx"
                      class="flex items-center gap-2"
                    >
                      <input
                        v-model="period.start"
                        type="time"
                        class="bh-time-input"
                      />
                      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                      <span
                        class="text-sm text-n-slate-10 flex items-center leading-none"
                      >
                        -
                      </span>
                      <input
                        v-model="period.end"
                        type="time"
                        class="bh-time-input"
                      />
                      <button
                        v-if="scheduleConfig[day].periods.length > 1"
                        class="w-6 h-6 flex items-center justify-center rounded text-n-slate-10 hover:text-n-ruby-9 hover:bg-n-ruby-3 transition-colors"
                        :title="
                          $t('TEAMS_SETTINGS.BUSINESS_HOURS.REMOVE_PERIOD')
                        "
                        @click="removePeriod(day, pIdx)"
                      >
                        <span class="i-lucide-x size-3.5" />
                      </button>
                    </div>
                    <button
                      class="text-xs text-n-brand font-medium hover:underline self-start"
                      @click="addPeriod(day)"
                    >
                      {{ $t('TEAMS_SETTINGS.BUSINESS_HOURS.ADD_PERIOD') }}
                    </button>
                  </template>
                  <template v-else>
                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                    <span class="text-xs text-n-slate-10 italic py-1.5">
                      {{ $t('TEAMS_SETTINGS.BUSINESS_HOURS.CLOSED') }}
                    </span>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════════════ -->
      <!-- SECTION 2: Outside Hours               -->
      <!-- ══════════════════════════════════════ -->
      <div
        v-if="businessHoursEnabled"
        class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
      >
        <div class="flex flex-col gap-1 items-start px-4 py-3">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.MESSAGE_LABEL') }}
          </h3>
        </div>

        <div class="px-4 py-3">
          <div class="flex flex-col gap-4">
            <!-- Message -->
            <div class="flex flex-col gap-1">
              <textarea
                v-model="outsideHoursMessage"
                class="block w-full border-none rounded-md shadow-sm bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand text-n-slate-12 sm:text-sm sm:leading-6 px-3 py-2 min-h-[72px] resize-y"
                :placeholder="
                  $t(
                    'TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.MESSAGE_PLACEHOLDER'
                  )
                "
              />
              <span class="text-xs text-n-slate-10">
                {{ $t('TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.MESSAGE_HINT') }}
              </span>
            </div>

            <!-- Fallback team -->
            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-12">
                  {{
                    $t('TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.FALLBACK_LABEL')
                  }}
                </label>
                <div class="isp-select-wrap w-full">
                  <select v-model="fallbackTeamId" class="isp-select">
                    <option :value="null">
                      {{
                        $t(
                          'TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.FALLBACK_NONE'
                        )
                      }}
                    </option>
                    <option
                      v-for="team in otherTeams"
                      :key="team.id"
                      :value="team.id"
                    >
                      {{ team.name }}
                    </option>
                  </select>
                  <span class="isp-select-arrow i-lucide-chevron-down" />
                </div>
                <span class="text-xs text-n-slate-10">
                  {{
                    $t('TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.FALLBACK_HINT')
                  }}
                </span>
              </div>

              <div v-if="fallbackTeamId" class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-12">
                  {{
                    $t(
                      'TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.FALLBACK_CLOSED_LABEL'
                    )
                  }}
                </label>
                <div class="flex gap-4 mt-2">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      v-model="fallbackClosedAction"
                      type="radio"
                      value="keep_fallback"
                      class="accent-n-brand"
                    />
                    <span class="text-sm text-n-slate-12">
                      {{
                        $t(
                          'TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.FALLBACK_KEEP_FALLBACK',
                          { team: fallbackTeamName }
                        )
                      }}
                    </span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      v-model="fallbackClosedAction"
                      type="radio"
                      value="keep_original"
                      class="accent-n-brand"
                    />
                    <span class="text-sm text-n-slate-12">
                      {{
                        $t(
                          'TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.FALLBACK_KEEP_ORIGINAL',
                          { team: currentTeamName }
                        )
                      }}
                    </span>
                  </label>
                </div>
                <span class="text-xs text-n-slate-10">
                  {{
                    $t(
                      'TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.FALLBACK_CLOSED_HINT'
                    )
                  }}
                </span>
              </div>
            </div>

            <!-- GK-HUB: Assign outside hours toggle -->
            <div class="mt-4 pt-4 border-t border-n-weak">
              <label class="flex items-start gap-2 cursor-pointer">
                <input
                  v-model="assignOutsideHours"
                  type="checkbox"
                  class="accent-n-brand mt-0.5"
                />
                <div class="flex flex-col gap-1">
                  <span class="text-sm text-n-slate-12">
                    {{
                      $t(
                        'TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.ASSIGN_OUTSIDE_LABEL'
                      )
                    }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{
                      $t(
                        'TEAMS_SETTINGS.BUSINESS_HOURS.OUTSIDE.ASSIGN_OUTSIDE_HINT'
                      )
                    }}
                  </span>
                </div>
              </label>
            </div>
            <!-- GK-HUB: end assign outside hours toggle -->
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════════════ -->
      <!-- SECTION 3: Holidays                    -->
      <!-- ══════════════════════════════════════ -->
      <div
        v-if="businessHoursEnabled"
        class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
      >
        <div class="flex flex-col gap-1 items-start px-4 py-3">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('TEAMS_SETTINGS.BUSINESS_HOURS.HOLIDAYS.TITLE') }}
          </h3>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ $t('TEAMS_SETTINGS.BUSINESS_HOURS.HOLIDAYS.SUBTITLE') }}
          </p>
        </div>

        <div class="px-4 py-3">
          <div class="flex items-center gap-2">
            <input
              v-model="respectHolidays"
              type="checkbox"
              class="accent-n-brand"
            />
            <span class="text-sm text-n-slate-12">
              {{ $t('TEAMS_SETTINGS.BUSINESS_HOURS.HOLIDAYS.RESPECT') }}
            </span>
          </div>
          <p class="text-xs text-n-slate-10 mt-1 ml-5">
            {{ $t('TEAMS_SETTINGS.BUSINESS_HOURS.HOLIDAYS.RESPECT_HINT') }}
          </p>
        </div>
      </div>

      <!-- ══════════════════════════════════════ -->
      <!-- Navigation Buttons                     -->
      <!-- ══════════════════════════════════════ -->
      <div class="flex justify-end gap-3 py-2">
        <NextButton
          variant="faded"
          :label="$t('TEAMS_SETTINGS.BUSINESS_HOURS.BACK')"
          @click="handleBack"
        />
        <NextButton
          v-if="isCreateMode && !businessHoursEnabled"
          variant="faded"
          :label="$t('TEAMS_SETTINGS.BUSINESS_HOURS.SKIP')"
          @click="handleSkip"
        />
        <NextButton
          :label="nextLabel"
          :is-loading="isSubmitting"
          :disabled="isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>

<style>
.bh-day-row {
  grid-template-columns: 90px 36px 1fr;
}

.bh-time-input {
  width: 100px;
  height: 30px !important;
  font-size: 12px !important;
  font-family: inherit;
  padding: 4px 8px;
  accent-color: #2e86c1;
  border-radius: 8px;
  border: 1px solid #e0e2e6;
  background-color: #f8f9fb;
  color: #4b5563;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
}

.bh-time-input:hover {
  border-color: #cbd0d6;
}

.bh-time-input:focus {
  border-color: #2e86c1;
}

body.dark .bh-time-input {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
  color-scheme: dark;
}

body.dark .bh-time-input:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}

/* ── ISP Select pattern (same as reports) ── */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}

.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 100%;
  height: auto;
  margin: 0;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 30px 6px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  background: none;
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}

.isp-select:hover {
  border-color: #cbd0d6;
}

.isp-select:focus {
  border-color: #2e86c1;
}

body.dark .isp-select {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}

body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}

body.dark .isp-select-arrow {
  color: #64748b;
}
</style>
